import { useEffect, useState } from 'react';
import type { Identity } from '../types';
import { errorMessage } from '../lib/db';
import { fetchMyEnvelopes } from '../lib/relay';

type Props = {
  me: Identity;
  onBack: () => void;
};

type ServerState = {
  loading: boolean;
  error: string | null;
  pendingEnvelopes: number;
  totalCiphertextBytes: number;
};

function shortHash(b64: string): string {
  return b64.replace(/[+/=]/g, '').slice(0, 8) + '…' + b64.replace(/[+/=]/g, '').slice(-4);
}

export function Inspector({ me, onBack }: Props) {
  const [state, setState] = useState<ServerState>({
    loading: true,
    error: null,
    pendingEnvelopes: 0,
    totalCiphertextBytes: 0,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const envelopes = await fetchMyEnvelopes(me.device_id);
        if (cancelled) return;
        const totalBytes = envelopes.reduce((sum, e) => sum + e.size_bytes, 0);
        setState({
          loading: false,
          error: null,
          pendingEnvelopes: envelopes.length,
          totalCiphertextBytes: totalBytes,
        });
      } catch (err) {
        if (!cancelled) {
          setState((prev) => ({
            ...prev,
            loading: false,
            error: errorMessage(err),
          }));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [me.id]);

  return (
    <div className="screen">
      <header className="topbar">
        <button className="btn btn-ghost btn-small" onClick={onBack} aria-label="Back">
          ← Back
        </button>
        <div className="topbar-center">
          <span className="me-name">Server inspector</span>
        </div>
        <span className="topbar-spacer" />
      </header>

      <main className="content">
        <section className="card">
          <p className="eyebrow">What our servers know about you</p>
          <h2 className="h-section">A short list, on purpose.</h2>

          <dl className="kv">
            <div className="kv-row">
              <dt>Username</dt>
              <dd>@{me.username}</dd>
            </div>
            <div className="kv-row">
              <dt>Identity id</dt>
              <dd className="kv-mono">{me.id}</dd>
            </div>
            <div className="kv-row">
              <dt>This device</dt>
              <dd className="kv-mono">
                {me.device_label} · {shortHash(me.device_id)}
              </dd>
            </div>
            <div className="kv-row">
              <dt>Device outer key</dt>
              <dd className="kv-mono">{shortHash(me.outer_pubkey_b64)}</dd>
            </div>
            <div className="kv-row">
              <dt>Pending envelopes in your mailbox</dt>
              <dd>
                {state.loading ? '…' : state.error ? '?' : state.pendingEnvelopes}
              </dd>
            </div>
            <div className="kv-row">
              <dt>Total ciphertext size on the server, for you</dt>
              <dd>
                {state.loading
                  ? '…'
                  : state.error
                    ? '?'
                    : `${state.totalCiphertextBytes} bytes (all unreadable)`}
              </dd>
            </div>
          </dl>

          {state.error && <p className="error">{state.error}</p>}
        </section>

        <section className="card card-negative">
          <p className="eyebrow">What our servers cannot know</p>
          <h2 className="h-section">The data we never have.</h2>

          <ul className="negative-list">
            <li><strong>Your secret key.</strong> It never leaves this device.</li>
            <li><strong>What you wrote.</strong> The relay only sees opaque bytes.</li>
            <li><strong>Who wrote to you.</strong> Sealed sender — no sender column.</li>
            <li><strong>Who you talk to.</strong> No contact graph. Groups live only on your phone.</li>
            <li><strong>When you read.</strong> Envelopes are deleted on fetch. No read receipts.</li>
            <li><strong>Whether you're online.</strong> No presence, no "last seen."</li>
          </ul>
        </section>

        <p className="footnote">
          The <code>envelopes</code> table has no <code>sender_id</code>,
          no <code>read_at</code>, no <code>contact_graph</code>. Just
          ciphertext in a mailbox, until you fetch it.
        </p>
      </main>
    </div>
  );
}
